import API from '../apiCreate'
import type {
  IntegrationsIndexResponse,
  IntegrationItemResponse,
  IntegrationProvider,
  IntegrationUsage,
  CreateIntegrationRequest,
  UpdateIntegrationRequest,
  ConnectIntegrationRequest,
  IntegrationChannelsResponse,
  IntegrationChannelItemResponse,
  UpdateIntegrationChannelRequest,
} from './integrations.types'

export type IntegrationsListParams = {
  provider?: IntegrationProvider
  usage?: IntegrationUsage
}

const buildIntegrationsIndexQuery = (
  providerOrParams?: IntegrationProvider | IntegrationsListParams,
): string => {
  const params: IntegrationsListParams =
    typeof providerOrParams === 'string'
      ? { provider: providerOrParams }
      : providerOrParams ?? {}

  const search = new URLSearchParams()
  if (params.provider) search.set('provider', params.provider)
  if (params.usage) search.set('usage', params.usage)
  const qs = search.toString()
  return qs ? `?${qs}` : ''
}

export const integrationsApi = {
  /** @param providerOrParams provider string (legacy) или `{ provider, usage }` */
  getAll: (providerOrParams?: IntegrationProvider | IntegrationsListParams) =>
    API.get<IntegrationsIndexResponse>(
      `integrations/index${buildIntegrationsIndexQuery(providerOrParams)}`,
    ),

  view: (id: number) =>
    API.get<IntegrationItemResponse>(`integrations/view/${id}`),

  create: (data: CreateIntegrationRequest) =>
    API.post<IntegrationItemResponse>('integrations/create', data),

  connect: (id: number, data?: ConnectIntegrationRequest) =>
    API.post<IntegrationItemResponse>(`integrations/connect/${id}`, data ?? {}),

  update: (id: number, data: UpdateIntegrationRequest) =>
    API.put<IntegrationItemResponse>(`integrations/update/${id}`, data),

  disable: (id: number) =>
    API.post<IntegrationItemResponse>(`integrations/disable/${id}`),

  /**
   * Без `usage` — удаляет всю интеграцию.
   * С `usage` — снимает только это назначение; при последнем usage удалит целиком.
   */
  delete: (id: number, options?: { usage?: IntegrationUsage }) => {
    const qs = options?.usage
      ? `?usage=${encodeURIComponent(options.usage)}`
      : ''
    return API.delete<IntegrationItemResponse>(`integrations/delete/${id}${qs}`)
  },

  getChannels: (integrationId: number) =>
    API.get<IntegrationChannelsResponse>(
      `integrations/channels/${integrationId}`,
    ),

  updateChannel: (
    integrationId: number,
    channelId: number,
    data: UpdateIntegrationChannelRequest,
  ) =>
    API.patch<IntegrationChannelItemResponse>(
      `integrations/update-channel/${integrationId}?channelId=${channelId}`,
      data,
    ),
}
